// src/components/pdf-editor/TextOverlay.js
/**
 * Text Overlay v2 – Precise Draggable/Editable Text Element
 *
 * Positioned in PDF coordinates, scaled for display.
 * Features: drag, resize (8 handles), inline editing (double-click).
 * All position changes are converted back to PDF points.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';

const HANDLE_SIZE = 8;
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_CURSORS = {
  nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize', e: 'e-resize',
  se: 'se-resize', s: 's-resize', sw: 'sw-resize', w: 'w-resize',
};

const TextOverlay = ({ element, scale, selected, onSelect, onUpdate, onInteractionEnd }) => {
  const [editing, setEditing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null);
  const textareaRef = useRef(null);
  const dragStartRef = useRef(null);

  // Display position/size
  const dx = element.x * scale;
  const dy = element.y * scale;
  const dw = element.w * scale;
  const dh = element.h * scale;

  // Determine if this PDF-origin element has been modified
  const isPdfOrigin = element.origin === 'pdf';
  const isModified = isPdfOrigin && (
    element.text !== element.origText ||
    element.x !== element.origX || element.y !== element.origY ||
    element.w !== element.origW || element.h !== element.origH ||
    element.committed === false
  );

  // Modified PDF-origin: white bg to cover old page image text + visible text color
  // Unmodified PDF-origin: transparent (page image shows text)
  // User-added: always visible
  const bgColor = isPdfOrigin
    ? (isModified ? '#ffffff' : (selected ? 'rgba(59,130,246,0.06)' : 'transparent'))
    : (selected ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.85)');

  /* ── Drag ── */
  const handleMouseDown = useCallback((e) => {
    if (editing) return;
    e.stopPropagation();
    onSelect();
    dragStartRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: element.x, origY: element.y,
    };
    setDragging(true);
  }, [editing, element.x, element.y, onSelect]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const ds = dragStartRef.current;
      if (!ds) return;
      const deltaX = (e.clientX - ds.startX) / scale;
      const deltaY = (e.clientY - ds.startY) / scale;
      onUpdate({ ...element, x: ds.origX + deltaX, y: ds.origY + deltaY });
    };
    const handleUp = () => {
      setDragging(false);
      onInteractionEnd();
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [dragging, element, scale, onUpdate, onInteractionEnd]);

  /* ── Resize ── */
  const handleResizeStart = useCallback((e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    dragStartRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: element.x, origY: element.y,
      origW: element.w, origH: element.h,
    };
    setResizing(handle);
  }, [element]);

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (e) => {
      const ds = dragStartRef.current;
      if (!ds) return;
      const deltaX = (e.clientX - ds.startX) / scale;
      const deltaY = (e.clientY - ds.startY) / scale;
      let { origX: x, origY: y, origW: w, origH: h } = ds;

      if (resizing.includes('e')) w = Math.max(20, ds.origW + deltaX);
      if (resizing.includes('w')) { w = Math.max(20, ds.origW - deltaX); x = ds.origX + (ds.origW - w); }
      if (resizing.includes('s')) h = Math.max(10, ds.origH + deltaY);
      if (resizing.includes('n')) { h = Math.max(10, ds.origH - deltaY); y = ds.origY + (ds.origH - h); }

      onUpdate({ ...element, x, y, w, h });
    };
    const handleUp = () => { setResizing(null); onInteractionEnd(); };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
  }, [resizing, element, scale, onUpdate, onInteractionEnd]);

  /* ── Double-click to edit ── */
  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleBlur = useCallback(() => {
    setEditing(false);
    onInteractionEnd();
  }, [onInteractionEnd]);

  const handleTextChange = useCallback((e) => {
    onUpdate({ ...element, text: e.target.value, committed: false });
  }, [element, onUpdate]);

  /* ── Handle positions ── */
  const getHandlePos = (handle) => {
    const half = HANDLE_SIZE / 2;
    const pos = { top: -half, left: -half };
    if (handle.includes('s')) pos.top = dh - half;
    if (handle.includes('e')) pos.left = dw - half;
    if (handle === 'n' || handle === 's') pos.left = dw / 2 - half;
    if (handle === 'w' || handle === 'e') pos.top = dh / 2 - half;
    return pos;
  };

  const fontSize = (element.style?.fontSize || 12) * scale;

  return (
    <div
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      style={{
        position: 'absolute',
        left: dx, top: dy, width: dw, height: dh,
        background: bgColor,
        border: selected ? '1.5px solid #3b82f6' : '1px solid transparent',
        borderRadius: 2,
        cursor: editing ? 'text' : dragging ? 'grabbing' : 'grab',
        zIndex: selected ? 20 : (isPdfOrigin ? 5 : 10),
        transition: dragging || resizing ? 'none' : 'border-color 0.15s',
        boxSizing: 'border-box',
        // Hover effect for PDF text
        ...(isPdfOrigin && !selected ? {
          ':hover': { background: 'rgba(59,130,246,0.04)' }
        } : {}),
      }}
      onMouseEnter={(e) => {
        if (!selected && isPdfOrigin) {
          e.currentTarget.style.background = 'rgba(59,130,246,0.04)';
          e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!selected && isPdfOrigin) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
        }
      }}
    >
      {/* Text display / editor */}
      {editing ? (
        <textarea
          ref={textareaRef}
          value={element.text || ''}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.target.blur(); } }}
          style={{
            width: '100%', height: '100%', border: 'none', outline: 'none',
            background: 'rgba(255,255,255,0.95)', resize: 'none', padding: 2,
            fontFamily: element.style?.fontFamily || 'sans-serif',
            fontSize, fontWeight: element.style?.fontWeight || 'normal',
            fontStyle: element.style?.fontStyle || 'normal',
            color: element.style?.color || '#000',
            textAlign: element.style?.textAlign || 'left',
            lineHeight: 1.2, letterSpacing: 0, overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <div style={{
          width: '100%', height: '100%', overflow: 'hidden',
          fontFamily: element.style?.fontFamily || 'sans-serif',
          fontSize, fontWeight: element.style?.fontWeight || 'normal',
          fontStyle: element.style?.fontStyle || 'normal',
          color: (isPdfOrigin && !isModified) ? 'transparent' : (element.style?.color || '#000'),
          textAlign: element.style?.textAlign || 'left',
          lineHeight: 1.2, letterSpacing: 0,
          padding: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          userSelect: 'none', pointerEvents: 'none',
          boxSizing: 'border-box',
        }}>
          {element.text}
        </div>
      )}

      {/* Resize handles */}
      {selected && !editing && HANDLES.map(h => (
        <div
          key={h}
          onMouseDown={(e) => handleResizeStart(e, h)}
          style={{
            position: 'absolute', ...getHandlePos(h),
            width: HANDLE_SIZE, height: HANDLE_SIZE,
            background: '#fff', border: '1.5px solid #3b82f6',
            borderRadius: 2, cursor: HANDLE_CURSORS[h],
            zIndex: 30,
          }}
        />
      ))}
    </div>
  );
};

export default TextOverlay;
